import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import SectionHelp from '@/components/help/SectionHelp';
import FieldHelp from '@/components/help/FieldHelp';
import helpContent from '@/help/helpContent';
import { CalendarDays, Clock } from 'lucide-react';

export default function AgendaNew() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [deals, setDeals] = useState([]);
  const [locations, setLocations] = useState([]);
  const [services, setServices] = useState([]);
  const [resources, setResources] = useState([]);
  const [labels, setLabels] = useState({});
  const [loading, setLoading] = useState(false);
  const [resourceFull, setResourceFull] = useState(false);
  const [joiningWaitlist, setJoiningWaitlist] = useState(false);

  const [teamUsers, setTeamUsers] = useState([]);

  const [form, setForm] = useState(() => {
    const date = searchParams.get('date'); // yyyy-MM-dd
    const time = searchParams.get('time') || '10:00';
    let starts_at = '';
    if (date) {
      starts_at = `${date}T${time.length === 5 ? time : '10:00'}`;
    }
    return {
      crm_lead_id: searchParams.get('lead_id') || '',
      crm_deal_id: searchParams.get('deal_id') || '',
      starts_at,
      duration_minutes: 30, location_id: '', notes: '',
      service_id: '', resource_id: '', assigned_to_user_id: '',
      advance_deal_stage: true,
    };
  });

  // Al elegir servicio, filtrar recursos a los asociados (si el servicio los define)
  const resourcesForService = (() => {
    if (!form.service_id) return resources;
    const svc = services.find(s => s.id === form.service_id);
    const allowed = svc?.resource_ids || [];
    if (!allowed.length) return resources;
    return resources.filter(r => allowed.includes(r.id));
  })();

  useEffect(() => {
    api.get('/tenant/crm/leads?status=active&page_size=50').then(r => setLeads(r.data?.items || [])).catch(() => {});
    api.get('/tenant/locations').then(r => setLocations(r.data?.items || r.data || [])).catch(() => {});
    api.get('/tenant/agenda/status').then(r => setLabels(r.data?.vertical_labels || {})).catch(() => {});
    api.get('/tenant/agenda/services').then(r => setServices((r.data?.items || []).filter(s => s.is_active))).catch(() => {});
    api.get('/tenant/agenda/resources').then(r => setResources((r.data?.items || []).filter(r2 => r2.is_active))).catch(() => {});
    // S57 (4b/N) — sin esto, una cita manual para un lead sin vendedor
    // asignado no tiene a nadie a quien sincronizar en Google Calendar.
    api.get('/tenant/team').then(r => setTeamUsers(r.data?.items || r.data || [])).catch(() => {});
  }, []);

  const handleServiceChange = (serviceId) => {
    const svc = services.find(s => s.id === serviceId);
    const allowed = svc?.resource_ids || [];
    let resource_id = form.resource_id;
    if (allowed.length && resource_id && !allowed.includes(resource_id)) {
      resource_id = allowed[0] || '';
    } else if (allowed.length === 1) {
      resource_id = allowed[0];
    }
    setForm({
      ...form, service_id: serviceId, resource_id,
      // A service's own duration is a starting suggestion, not a lock —
      // the person booking can still override it below.
      duration_minutes: svc ? svc.duration_minutes : form.duration_minutes,
    });
  };

  useEffect(() => {
    if (form.crm_lead_id) {
      api.get(`/tenant/crm/deals?status=open`).then(r => {
        setDeals((r.data?.items || []).filter(d => d.crm_lead_id === form.crm_lead_id));
      }).catch(() => {});
    } else { setDeals([]); }
  }, [form.crm_lead_id]);

  const apptLabel = labels.appointment || 'Cita';

  const handleSave = async () => {
    if (!form.crm_lead_id) { toast.error('Selecciona un lead'); return; }
    if (!form.starts_at) { toast.error('Selecciona fecha y hora'); return; }
    setLoading(true);
    setResourceFull(false);
    try {
      const payload = {
        crm_lead_id: form.crm_lead_id,
        starts_at: new Date(form.starts_at).toISOString(),
        duration_minutes: form.duration_minutes,
        notes: form.notes || null,
        advance_deal_stage: form.advance_deal_stage,
      };
      if (form.crm_deal_id) payload.crm_deal_id = form.crm_deal_id;
      if (form.location_id) payload.location_id = form.location_id;
      if (form.service_id) payload.service_id = form.service_id;
      if (form.resource_id) payload.resource_id = form.resource_id;
      if (form.assigned_to_user_id) payload.assigned_to_user_id = form.assigned_to_user_id;
      await api.post('/tenant/agenda/appointments', payload);
      toast.success(`${apptLabel} agendada`);
      navigate('/tenant/agenda/calendar');
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.error === 'resource_full' && form.resource_id) {
        setResourceFull(true);
        toast.error(detail.message);
      } else {
        toast.error((typeof detail === 'string' ? detail : detail?.message) || 'Error al agendar');
      }
    }
    finally { setLoading(false); }
  };

  const handleJoinWaitlist = async () => {
    setJoiningWaitlist(true);
    try {
      await api.post('/tenant/agenda/waitlist', {
        crm_lead_id: form.crm_lead_id,
        resource_id: form.resource_id,
        service_id: form.service_id || null,
        desired_starts_at: new Date(form.starts_at).toISOString(),
        duration_minutes: form.duration_minutes,
        notes: form.notes || null,
      });
      toast.success('Agregado a la lista de espera — te avisamos si se libera un espacio');
      navigate('/tenant/agenda/resources');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Error al agregar a la lista de espera'); }
    finally { setJoiningWaitlist(false); }
  };

  const hn = helpContent.agenda_new;
  return (
    <div className="space-y-4" data-testid="agenda-new">
      <h2 className="text-sm font-bold flex items-center gap-2"><CalendarDays className="w-4 h-4" /> Nueva {apptLabel.toLowerCase()}</h2>
      <SectionHelp
        module="agenda_new"
        title={hn.title}
        description={hn.description}
        whatItDoesNot={hn.whatItDoesNot}
      />

      <div className="bg-white border rounded-xl p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FieldHelp label={`${labels.lead || 'Lead'} *`} help={hn.fields.crm_lead_id.help} htmlFor="agenda-new-lead">
            <select id="agenda-new-lead" value={form.crm_lead_id} onChange={e => setForm({...form, crm_lead_id: e.target.value, crm_deal_id: ''})}
              className="w-full border rounded px-2 py-1.5 text-sm" data-testid="agenda-new-lead">
              <option value="">Seleccionar...</option>
              {leads.map(l => <option key={l.id} value={l.id}>{l.display_name} ({l.email || l.phone || ''})</option>)}
            </select>
          </FieldHelp>
          <FieldHelp label="Oportunidad (opcional)" help={hn.fields.crm_deal_id.help} htmlFor="agenda-new-deal">
            <select id="agenda-new-deal" value={form.crm_deal_id} onChange={e => setForm({...form, crm_deal_id: e.target.value})}
              className="w-full border rounded px-2 py-1.5 text-sm" data-testid="agenda-new-deal" disabled={!form.crm_lead_id}>
              <option value="">Sin oportunidad</option>
              {deals.map(d => <option key={d.id} value={d.id}>{d.title} ({d.stage_key})</option>)}
            </select>
          </FieldHelp>
          {services.length > 0 && (
            <FieldHelp label="Servicio (opcional)" help={hn.fields.service_id.help} htmlFor="agenda-new-service">
              <select id="agenda-new-service" value={form.service_id} onChange={e => handleServiceChange(e.target.value)}
                className="w-full border rounded px-2 py-1.5 text-sm" data-testid="agenda-new-service">
                <option value="">Sin servicio específico</option>
                {services.map(s => <option key={s.id} value={s.id}>{s.name} ({s.duration_minutes} min)</option>)}
              </select>
            </FieldHelp>
          )}
          {resources.length > 0 && (
            <FieldHelp label="Recurso (opcional)" help={hn.fields.resource_id.help} htmlFor="agenda-new-resource">
              <select id="agenda-new-resource" value={form.resource_id} onChange={e => setForm({...form, resource_id: e.target.value})}
                className="w-full border rounded px-2 py-1.5 text-sm" data-testid="agenda-new-resource">
                <option value="">Sin recurso específico</option>
                {resourcesForService.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </FieldHelp>
          )}
          {teamUsers.length > 0 && (
            <FieldHelp label="Vendedor asignado (opcional)" help={hn.fields.assigned_to_user_id.help} htmlFor="agenda-new-assignee">
              <select id="agenda-new-assignee" value={form.assigned_to_user_id} onChange={e => setForm({...form, assigned_to_user_id: e.target.value})}
                className="w-full border rounded px-2 py-1.5 text-sm" data-testid="agenda-new-assignee">
                <option value="">Usar el del lead (si tiene)</option>
                {teamUsers.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
              </select>
            </FieldHelp>
          )}
          <FieldHelp label="Fecha y hora *" help={hn.fields.starts_at.help} htmlFor="agenda-new-datetime">
            <input id="agenda-new-datetime" type="datetime-local" value={form.starts_at} onChange={e => setForm({...form, starts_at: e.target.value})}
              className="w-full border rounded px-2 py-1.5 text-sm" data-testid="agenda-new-datetime" />
          </FieldHelp>
          <FieldHelp label="Duración" help={hn.fields.duration_minutes.help} htmlFor="agenda-new-duration">
            <select id="agenda-new-duration" value={form.duration_minutes} onChange={e => setForm({...form, duration_minutes: +e.target.value})}
              className="w-full border rounded px-2 py-1.5 text-sm" data-testid="agenda-new-duration">
              {[15, 30, 45, 60, 90, 120].map(d => <option key={d} value={d}>{d} min</option>)}
            </select>
          </FieldHelp>
          {locations.length > 0 && (
            <div>
              <label className="text-xs text-[#64748B]">{labels.location || 'Ubicación'}</label>
              <select value={form.location_id} onChange={e => setForm({...form, location_id: e.target.value})}
                className="w-full border rounded px-2 py-1.5 text-sm" data-testid="agenda-new-location">
                <option value="">Sin ubicación</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
          )}
          <div className="md:col-span-2">
            <FieldHelp label="Notas" help={hn.fields.notes.help} htmlFor="agenda-new-notes">
              <Textarea id="agenda-new-notes" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
                placeholder="Notas opcionales..." data-testid="agenda-new-notes" />
            </FieldHelp>
          </div>
        </div>
        {form.crm_deal_id && (
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={form.advance_deal_stage} onChange={e => setForm({...form, advance_deal_stage: e.target.checked})} />
            Mover oportunidad a etapa &quot;{apptLabel} agendada&quot;
          </label>
        )}
        {resourceFull && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 flex items-center justify-between gap-3" data-testid="agenda-new-resource-full">
            <div className="flex items-center gap-2 text-xs text-amber-800">
              <Clock className="w-4 h-4 flex-shrink-0" />
              Ese recurso está lleno en ese horario. Puedes agregar a este cliente a la lista de espera — si alguien cancela, se le agenda automáticamente.
            </div>
            <Button size="sm" variant="outline" onClick={handleJoinWaitlist} disabled={joiningWaitlist}
              className="flex-shrink-0" data-testid="agenda-new-join-waitlist">
              {joiningWaitlist ? 'Agregando...' : 'Agregar a lista de espera'}
            </Button>
          </div>
        )}
        <div className="flex gap-2 justify-end">
          <Button size="sm" variant="outline" onClick={() => navigate(-1)}>Cancelar</Button>
          <Button size="sm" onClick={handleSave} disabled={loading || !form.crm_lead_id || !form.starts_at} data-testid="agenda-new-save">
            {loading ? 'Guardando...' : `Agendar ${apptLabel.toLowerCase()}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
